import { useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { PedidoDetalhe } from '../../../../@types/entity/pedido-detalhe-entity';
import { ItemBusca, SolicitacaoItem } from 'service/api/rest/fluxo-aprovacao-pedido-usecase';
import { FluxoAprovacaoPedidoOperadorPage } from '../fluxo-aprovacao-pedido-operador-page';

const ModalDetalheStyled = styled(Modal)`
  .modal-dialog {
    max-width: 90vw;
    width: 90vw;
  }
`;

type Props = {
  show: boolean;
  detalhe: PedidoDetalhe | null;
  /** Nome do pedido (origem: PedidoResumo.nome da linha da lista); exibido no header da visualização. */
  nome?: string;
  disabled?: boolean;
  onFechar: () => void;
  onEnviar: (idPedido: number) => void;
  onAprovar: (idPedido: number) => void;
  onValidar: (idPedido: number) => void;
  onReprovar: (idPedido: number, justificativa: string) => void;
  onEditar: (idPedido: number, solicitacoes: SolicitacaoItem[], justificativa: string) => void;
  onGerar: (idPedido: number) => void;
  /** Decide (aceitar/recusar em lote) as solicitacoes item-a-item pendentes (§4.5). */
  onDecidir?: (idPedido: number, idsAceitas: number[], idsRecusadas: number[]) => void;
  /** Retira uma solicitacao pendente da propria autoria (§4.5 / RN18). */
  onRetirar?: (idPedido: number, idSolicitacao: number) => void;
  /** Busca (typeahead) de itens disponíveis da filial do pedido, usada no modo edição. */
  onBuscarItens?: (termo: string) => Promise<ItemBusca[]>;
};

/**
 * Modal de detalhe do pedido do Analítico. Abre a partir do botão "Abrir"
 * da lista (em vez de navegar para outra página) e reaproveita a lógica/UI da tela do operador
 * (itens + ações Aprovar/Editar/Reprovar/Gerar) dentro de um Modal, espelhando o padrão
 * da tela legada (visualização em modal). Componente próprio da tela nova (autocontido).
 */
export function FluxoAprovacaoPedidoModalDetalhe(props: Props) {
  const { show, detalhe, nome, disabled, onFechar } = props;
  const { onEnviar, onAprovar, onValidar, onReprovar, onEditar, onGerar, onBuscarItens } = props;
  const { onDecidir, onRetirar } = props;
  const corpoRef = useRef<HTMLDivElement>(null);

  /**
   * As ações que mantêm a modal aberta recarregam o detalhe, e o botão que
   * acabou de ser clicado deixa de existir (a linha decidida perde as ações). O foco cairia no
   * `<body>`, jogando a navegação por teclado para fora da modal — então, quando o detalhe é
   * trocado e o foco se perdeu, ele volta para o corpo da modal.
   */
  useEffect(() => {
    if (!show || !detalhe) {
      return;
    }
    const focado = document.activeElement;
    if (!focado || focado === document.body) {
      corpoRef.current?.focus();
    }
  }, [show, detalhe]);

  return (
    <ModalDetalheStyled
      show={show}
      onHide={onFechar}
      size="xl"
      backdrop="static"
      dialogClassName="modal-detalhe-pedido"
    >
      <Modal.Header closeButton>
        <Modal.Title>Detalhe do Pedido de Compra</Modal.Title>
      </Modal.Header>
      <Modal.Body ref={corpoRef} tabIndex={-1}>
        {detalhe && (
          <FluxoAprovacaoPedidoOperadorPage
            detalhe={detalhe}
            nome={nome}
            disabled={disabled}
            onEnviar={onEnviar}
            onAprovar={onAprovar}
            onValidar={onValidar}
            onReprovar={onReprovar}
            onEditar={onEditar}
            onDecidir={onDecidir}
            onRetirar={onRetirar}
            onGerar={onGerar}
            onBuscarItens={onBuscarItens}
          />
        )}
      </Modal.Body>
    </ModalDetalheStyled>
  );
}

export default FluxoAprovacaoPedidoModalDetalhe;
